// ----------------------------------------------------------------------
// (c) Copyright 2020, Dark Star Systems, Inc.  All rights reserved.
// This file may contain proprietary and confidential information.
// DO NOT COPY or distribute in any form without prior written consent.
// ----------------------------------------------------------------------

import * as Three from 'three'
import * as NGLViewer from 'ngl'
import {EffectComposer} from 'three/examples/jsm/postprocessing/EffectComposer'
// import {HalftonePass} from 'three/examples/jsm/postprocessing/HalftonePass'
// import {FilmPass} from 'three/examples/jsm/postprocessing/FilmPass'
import {ShaderPass} from 'three/examples/jsm/postprocessing/ShaderPass'
import {RenderPass, RenderCallback} from '@/effects/RenderPass' // our custom version
import {vignetteMaterial, vignetteShader} from '@/effects/vignette'
import type {PostEffectParams} from '@/store/shots'
import {isEqual} from 'lodash'

/** Use EffectComposer to do postprocessing.
    - Set up composer, render pass, and postprocessing passes
    - Wrap `stage.viewer.render` with our render code, because with composer
      you have to call composer.render() rather than renderer.render().
    - The wrapper sets up things in the renderPass and then calls composer.render()
    - That eventually invokes our renderPass (modified version of the three.js example)
    - renderPass calls the renderCallback we set up
    - the renderCallback calls the real NGL render method
    - binding it to the viewer, and passing it the original args passed to stage.viewer.render
*/
export class PostprocessEffects {
  composer: EffectComposer
  renderPass: RenderPass
  vignettePass: ShaderPass

  constructor(stage: NGLViewer.Stage) {
    this.composer = new EffectComposer(stage.viewer.renderer)
    // we color-manage, so set textures to sRGB
    this.composer.renderTarget1.texture.encoding = Three.sRGBEncoding
    this.composer.renderTarget2.texture.encoding = Three.sRGBEncoding

    const realRender = stage.viewer.render
    const renderCallback: RenderCallback = (pass: RenderPass) => {
      // pass through picking arg (should always be false), but more importantly add the current pass's render target.
      // Cast here is because getRenderTarget() returns a generic RenderTarget, but we know it's a WebGLRenderTarget.
      const target = stage.viewer.renderer.getRenderTarget() as Three.WebGLRenderTarget || undefined
      realRender.bind(stage.viewer)(pass.renderArgs.picking, target)
    }
    this.renderPass = new RenderPass(stage.viewer.scene,
                                     stage.viewer.camera,
                                     renderCallback)
    this.renderPass.clear = false
    this.composer.addPass(this.renderPass)
    // composer.addPass(new HalftonePass(1, 1, {}))
    // composer.addPass(new FilmPass())
    this.vignettePass = new ShaderPass(vignetteMaterial)
    this.composer.addPass(this.vignettePass)

    // patch stage.viewer.render to be our wrapper
    stage.viewer.render = (picking = false) => {
      if (picking) {
        realRender.bind(stage.viewer, picking)()     // don't use composer
      } else {
        this.renderPass.scene = stage.viewer.scene
        this.renderPass.camera = stage.viewer.camera
        this.renderPass.renderArgs = {picking} // remember args so callback can use them
        this.composer.render()
      }
    }
  }

  serialize() {
    return ['width', 'height', 'radius', 'edgeWidth', 'darkness', 'scaleWidth']
      .map(name => vignetteMaterial.uniforms[name].value)
  }

  /** Set the params; return true if anything changed */
  setParams(width: number, height: number, params: PostEffectParams) {
    const initial = this.serialize()
    this.vignettePass.enabled = (params.vignetteDarkness.value > 0.0001)
    vignetteMaterial.uniforms.width.value = width
    vignetteMaterial.uniforms.height.value = height
    vignetteMaterial.uniforms.radius.value = params.vignetteRadius.value
    vignetteMaterial.uniforms.edgeWidth.value = params.vignetteSoftness.value
    vignetteMaterial.uniforms.darkness.value = params.vignetteDarkness.value
    vignetteMaterial.uniforms.scaleWidth.value = params.vignetteWidth.value
    const final = this.serialize()
    return !isEqual(initial, final)
  }
}
